import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  InputBase,
  Paper,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  styled,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { ScoreBandValue, ScoreBandResult, ScoreBandMode } from "../@types/services/inspStatusService";
import { t } from "i18next";

export interface ScoreBandControlProps {
  /** 전체 score 목록. 정렬돼 있지 않아도 됩니다.
   *  생략하거나 배열이 아니면 데모 분포로 렌더합니다(프리뷰용). */
  scores?: number[];
  /** 제어 컴포넌트로 쓸 때 */
  value?: ScoreBandValue;
  /** 비제어 초기값 */
  defaultValue?: Partial<ScoreBandValue>;
  /** 값이 바뀔 때마다 호출 (계산된 밴드 결과 포함) */
  onChange?: (value: ScoreBandValue, result: ScoreBandResult) => void;
  /** 비율 프리셋 버튼 */
  presets?: number[];
  /** 확대 레인에서 손잡이를 끌어 비율을 조절할 수 있게 함 */
  zoomResizable?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

/* ────────────────────────────── 계산 (변경 없음) ────────────────────────────── */

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const r4 = (n: number) => +n.toFixed(4);
const f4 = (n: number) => n.toFixed(4);

function lowerBound(arr: number[], x: number): number {
  let lo = 0,
    hi = arr.length;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (arr[m] < x) lo = m + 1;
    else hi = m;
  }
  return lo;
}
function upperBound(arr: number[], x: number): number {
  let lo = 0,
    hi = arr.length;
  while (lo < hi) {
    const m = (lo + hi) >> 1;
    if (arr[m] <= x) lo = m + 1;
    else hi = m;
  }
  return lo;
}

/** 기준값에서 가까운 순으로 pct% 를 담는 밴드 계산 */
export function computeBand(
  sorted: number[],
  anchor: number,
  pct: number,
  mode: ScoreBandMode = "near",
): ScoreBandResult {
  const total = sorted.length;
  const base: ScoreBandResult = { lo: anchor, hi: anchor, eps: 0, count: 0, k: 0, nBelow: 0, nAbove: 0, total };
  if (!total) return base;

  const k = Math.max(1, Math.round((total * clamp(pct, 0, 100)) / 100));
  let i = upperBound(sorted, anchor) - 1; // 기준 이하
  let j = upperBound(sorted, anchor); // 기준 초과
  let lo = anchor,
    hi = anchor,
    nBelow = 0,
    nAbove = 0;

  const takeBelow = () => {
    lo = sorted[i];
    i--;
    nBelow++;
  };
  const takeAbove = () => {
    hi = sorted[j];
    j++;
    nAbove++;
  };

  if (mode === "balanced") {
    const half = Math.ceil(k / 2);
    while (nBelow < half && i >= 0) takeBelow();
    while (nAbove < half && j < total) takeAbove();
    // 초과분은 먼 쪽부터 되돌림
    while (nBelow + nAbove > k) {
      const dl = nBelow > 0 ? anchor - lo : -Infinity;
      const dr = nAbove > 0 ? hi - anchor : -Infinity;
      if (dl >= dr) {
        i++;
        nBelow--;
        lo = nBelow > 0 ? sorted[i + 1] : anchor;
      } else {
        j--;
        nAbove--;
        hi = nAbove > 0 ? sorted[j - 1] : anchor;
      }
    }
  } else {
    for (let n = 0; n < k; n++) {
      const dl = i >= 0 ? anchor - sorted[i] : Infinity;
      const dr = j < total ? sorted[j] - anchor : Infinity;
      if (dl === Infinity && dr === Infinity) break;
      if (dl <= dr) takeBelow();
      else takeAbove();
    }
  }

  const count = Math.max(0, upperBound(sorted, hi) - lowerBound(sorted, lo));
  return {
    lo: r4(lo),
    hi: r4(hi),
    eps: Math.max(anchor - lo, hi - anchor),
    count,
    k,
    nBelow,
    nAbove,
    total,
  };
}

/* ──────────────── macOS 토큰 (테마의 light/dark 를 따라감) ──────────────── */

const MONO = `ui-monospace, "SF Mono", SFMono-Regular, Menlo, monospace`;
const SANS = `-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", Arial, sans-serif`;

function mac(theme: Theme) {
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    blue: dark ? "#0A84FF" : "#007AFF",
    pink: dark ? "#FF375F" : "#FF2D55",
    hair: dark ? "rgba(255,255,255,.14)" : "rgba(0,0,0,.09)",
    edge: dark ? "rgba(255,255,255,.22)" : "rgba(0,0,0,.18)",
    text: dark ? "#F5F5F7" : "#1D1D1F",
    text2: dark ? "#A1A1A6" : "#6E6E73",
    text3: dark ? "#7C7C81" : "#98989D",
    chrome: dark ? "#1E1E20" : "#F7F7F9",
    lane: dark ? "#242427" : "#FFFFFF",
    ruler: dark ? "#1A1A1C" : "#FCFCFD",
    field: dark ? "#2C2C2E" : "#FFFFFF",
    track: dark ? "#48484A" : "#DEDEE2",
    idle: dark ? "#55555C" : "#CBD0D8",
    tick: dark ? "#6C6C72" : "#AEB4BD",
    segBg: dark ? "#2C2C2E" : "#E9E9EC",
    segOn: dark ? "#4C4C50" : "#FFFFFF",
    glass: dark ? "rgba(30,30,32,.78)" : "rgba(255,255,255,.8)",
    mono: MONO,
  };
}

const focusRing = (c: ReturnType<typeof mac>) => `0 0 0 3.5px ${alpha(c.blue, 0.28)}`;

/* ────────────────────────────── styled ────────────────────────────── */

const Shell = styled(Paper, { shouldForwardProp: (p) => p !== "isDisabled" })<{ isDisabled?: boolean }>(({
  theme,
  isDisabled,
}) => {
  const c = mac(theme);
  return {
    fontFamily: SANS,
    fontSize: 13,
    color: c.text,
    WebkitFontSmoothing: "antialiased",
    backgroundColor: c.chrome,
    backgroundImage: "none",
    border: `.5px solid ${c.hair}`,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: `0 1px 2px ${alpha("#000", c.dark ? 0.4 : 0.05)}`,
    ...(isDisabled && { opacity: 0.45, pointerEvents: "none" as const }),
  };
});

const Label = styled(Typography)(({ theme }) => ({
  display: "block",
  fontFamily: SANS,
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1.3,
  color: mac(theme).text2,
}));

const Caption = styled(Typography)(({ theme }) => ({
  fontFamily: MONO,
  fontSize: 10,
  lineHeight: 1.5,
  color: mac(theme).text3,
  whiteSpace: "nowrap",
}));

const numberReset = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

/** 기준 score 입력: macOS 텍스트 필드 */
const AnchorField = styled(InputBase)(({ theme }) => {
  const c = mac(theme);
  return {
    width: "100%",
    padding: "5px 9px",
    color: c.text,
    backgroundColor: c.field,
    border: `.5px solid ${c.edge}`,
    borderRadius: 6,
    boxShadow: `inset 0 1px 1px ${alpha("#000", c.dark ? 0.25 : 0.04)}`,
    transition: theme.transitions.create(["box-shadow", "border-color"], { duration: 120 }),
    "& input": { fontFamily: MONO, fontSize: 15, fontWeight: 500, padding: 0, height: "auto" },
    "&.Mui-focused": { borderColor: c.blue, boxShadow: focusRing(c) },
    ...numberReset,
  };
});

/** 표시 비율 입력: 라벨 옆에 붙는 무테 필드 */
const PctField = styled(InputBase)(({ theme }) => {
  const c = mac(theme);
  return {
    width: "5.6ch",
    padding: "1px 2px",
    borderRadius: 4,
    "& input": { fontFamily: MONO, fontSize: 15, fontWeight: 600, color: c.blue, padding: 0, height: "auto" },
    "&.Mui-focused": { boxShadow: focusRing(c) },
    ...numberReset,
  };
});

const MacSlider = styled(Slider)(({ theme }) => {
  const c = mac(theme);
  return {
    height: 4,
    padding: "10px 0",
    color: c.blue,
    "& .MuiSlider-rail": {
      opacity: 1,
      backgroundColor: c.track,
      boxShadow: `inset 0 0 0 .5px ${alpha("#000", 0.07)}`,
    },
    "& .MuiSlider-track": { border: 0 },
    "& .MuiSlider-thumb": {
      width: 17,
      height: 17,
      backgroundColor: "var(--dk-surface, #fff)",
      boxShadow: `0 0 0 .5px ${alpha("#000", 0.2)}, 0 1px 3px ${alpha("#000", 0.28)}`,
      "&:hover, &.Mui-active": {
        boxShadow: `0 0 0 .5px ${alpha("#000", 0.2)}, 0 1px 4px ${alpha("#000", 0.34)}`,
      },
      "&.Mui-focusVisible": { boxShadow: `${focusRing(c)}, 0 1px 3px ${alpha("#000", 0.28)}` },
      "&::before": { boxShadow: "none" },
    },
    "& .MuiSlider-valueLabel": {
      fontFamily: MONO,
      fontSize: 11,
      padding: "2px 6px",
      borderRadius: 5,
      backgroundColor: c.dark ? "#3A3A3C" : "#1D1D1F",
    },
  };
});

/** macOS 세그먼티드 컨트롤 */
const Segmented = styled(ToggleButtonGroup)(({ theme }) => {
  const c = mac(theme);
  return {
    backgroundColor: c.segBg,
    borderRadius: 7,
    padding: 2,
    boxShadow: `inset 0 0 0 .5px ${alpha("#000", 0.05)}`,
    gap: 2,
    "& .MuiToggleButtonGroup-grouped": {
      margin: 0,
      border: 0,
      borderRadius: 5,
      "&:not(:first-of-type), &:first-of-type": { borderRadius: 5, marginLeft: 0 },
    },
  };
});

const SegButton = styled(ToggleButton)(({ theme }) => {
  const c = mac(theme);
  return {
    fontFamily: SANS,
    fontSize: 12,
    fontWeight: 400,
    textTransform: "none",
    lineHeight: 1.4,
    padding: "4px 11px",
    color: c.dark ? "#E5E5EA" : "#3A3A3C",
    whiteSpace: "nowrap",
    transition: theme.transitions.create(["background-color", "box-shadow"], { duration: 120 }),
    "&:hover": { backgroundColor: alpha(c.text, 0.06) },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: c.segOn,
      color: c.text,
      fontWeight: 500,
      boxShadow: `0 0 0 .5px ${alpha("#000", 0.06)}, 0 1px 2px ${alpha("#000", c.dark ? 0.35 : 0.14)}`,
    },
    "&.Mui-focusVisible": { boxShadow: focusRing(c) },
  };
});

/** 비율 프리셋 */
const PresetButton = styled(ToggleButton)(({ theme }) => {
  const c = mac(theme);
  return {
    fontFamily: MONO,
    fontSize: 11,
    lineHeight: 1.4,
    textTransform: "none",
    padding: "3px 9px",
    color: c.dark ? "#E5E5EA" : "#3A3A3C",
    backgroundColor: c.field,
    border: `.5px solid ${c.edge}`,
    borderRadius: 6,
    boxShadow: `0 1px 1px ${alpha("#000", c.dark ? 0.3 : 0.04)}`,
    "&:hover": { backgroundColor: c.dark ? "#3A3A3C" : "#F5F5F7" },
    "&.Mui-selected, &.Mui-selected:hover": {
      backgroundColor: c.blue,
      borderColor: "transparent",
      color: "#fff",
      boxShadow: `0 1px 2px ${alpha("#000", 0.18)}`,
    },
    "&.Mui-focusVisible": { boxShadow: focusRing(c) },
  };
});

/** 히스토그램 / 점 분포 레인 */
const Lane = styled(Box)(({ theme }) => {
  const c = mac(theme);
  return {
    position: "relative",
    height: 46,
    backgroundColor: c.lane,
    border: `.5px solid ${c.hair}`,
    borderRadius: 7,
    overflow: "hidden",
    cursor: "ew-resize",
    touchAction: "none",
    outline: "none",
    "&:focus-visible": { boxShadow: focusRing(c) },
    '&[data-zoom="true"]': { height: 34, backgroundColor: c.dark ? "#1F1F22" : "#F5F5F7" },
    '&[data-resizable="false"]': { cursor: "default" },
  };
});

const Bars = styled("div")({
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "flex-end",
  pointerEvents: "none",
});

const HBar = styled("div")(({ theme }) => {
  const c = mac(theme);
  return {
    flex: "1 1 0",
    minWidth: 0,
    backgroundColor: c.idle,
    '&[data-in="true"]': { backgroundColor: c.blue },
  };
});

const Tick = styled("div")(({ theme }) => {
  const c = mac(theme);
  return {
    position: "absolute",
    width: 1,
    backgroundColor: c.tick,
    pointerEvents: "none",
    '&[data-in="true"]': { backgroundColor: c.blue },
  };
});

const Band = styled("div")(({ theme }) => {
  const c = mac(theme);
  return {
    position: "absolute",
    top: 0,
    bottom: 0,
    pointerEvents: "none",
    backgroundColor: alpha(c.blue, 0.13),
    borderLeft: `1px solid ${c.blue}`,
    borderRight: `1px solid ${c.blue}`,
  };
});

const Anchor = styled("div")(({ theme }) => {
  const c = mac(theme);
  return {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 0,
    pointerEvents: "none",
    borderLeft: `1.5px solid ${c.pink}`,
    "&::before": {
      content: '""',
      position: "absolute",
      top: -1,
      left: -4.5,
      width: 9,
      height: 9,
      backgroundColor: c.pink,
      borderRadius: 2,
      transform: "rotate(45deg)",
      boxShadow: `0 1px 2px ${alpha("#000", 0.28)}`,
    },
  };
});

const Grip = styled("div")({
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: 8,
  borderRadius: 9,
  backgroundColor: "var(--dk-surface, #fff)",
  pointerEvents: "none",
  boxShadow: `0 0 0 .5px ${alpha("#000", 0.25)}, 0 1px 3px ${alpha("#000", 0.3)}`,
  "& i": { display: "block", width: 1, backgroundColor: alpha("#000", 0.3), margin: "0 auto" },
});

const LaneTag = styled("span")(({ theme }) => {
  const c = mac(theme);
  return {
    position: "absolute",
    right: 8,
    top: 5,
    zIndex: 2,
    fontFamily: SANS,
    fontSize: 10,
    color: c.text3,
    backgroundColor: c.glass,
    backdropFilter: "saturate(180%) blur(8px)",
    padding: "0 4px",
    borderRadius: 4,
    pointerEvents: "none",
  };
});

const AxisRow = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 10,
});

/* ─────────────────── 숫자 입력 (타이핑 중 값 튐 방지) ─────────────────── */

interface NumFieldProps {
  /** AnchorField / PctField 중 하나 */
  as: React.ComponentType<React.ComponentProps<typeof InputBase>>;
  display: string;
  min: number;
  max: number;
  step: number;
  ariaLabel: string;
  disabled?: boolean;
  onCommit: (v: number) => void;
}

const NumField: React.FC<NumFieldProps> = ({ as: Field, display, min, max, step, ariaLabel, disabled, onCommit }) => {
  const [txt, setTxt] = useState(display);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setTxt(display);
  }, [display, focused]);

  const commit = (raw: string) => {
    const v = parseFloat(raw);
    if (Number.isNaN(v)) {
      setTxt(display);
      return;
    }
    onCommit(clamp(v, min, max));
  };

  return (
    <Field
      type="number"
      value={txt}
      disabled={disabled}
      inputProps={{ min, max, step, "aria-label": ariaLabel }}
      onChange={(e) => {
        const s = e.target.value;
        setTxt(s);
        if (s !== "" && !s.endsWith(".") && !s.endsWith("-") && !Number.isNaN(parseFloat(s))) commit(s);
      }}
      onFocus={(e) => {
        setFocused(true);
        e.currentTarget.querySelector("input")?.select();
      }}
      onBlur={(e) => {
        setFocused(false);
        commit((e.target as HTMLInputElement).value);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          const el = e.target as HTMLInputElement;
          commit(el.value);
          el.blur();
        }
      }}
    />
  );
};

/* ────────────────────────────── 본체 ────────────────────────────── */

const BINS = 180;
type DragKind = "anchor" | "lo" | "hi" | null;

/** props 없이 렌더될 때(스토리북·프리뷰) 쓰는 샘플 분포 */
const DEMO_SCORES: number[] = (() => {
  let a = 20260814;
  const rnd = () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const g = (m: number, s: number) => {
    let u = 0,
      v2 = 0;
    while (!u) u = rnd();
    while (!v2) v2 = rnd();
    return m + s * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v2);
  };
  return Array.from({ length: 2000 }, () => clamp(r4(rnd() < 0.18 ? g(0.78, 0.07) : g(0.49, 0.13)), 0.0001, 0.9999));
})();

export default function ScoreBandControl({
  scores: scoresProp,
  value,
  defaultValue,
  onChange,
  presets = [1, 5, 7, 10, 25, 100],
  zoomResizable = true,
  disabled = false,
  className,
  style,
  sx,
}: ScoreBandControlProps) {
  const scores = Array.isArray(scoresProp) ? scoresProp : DEMO_SCORES;
  const sorted = useMemo(
    () => scores.filter((n) => typeof n === "number" && !Number.isNaN(n)).sort((a, b) => a - b),
    [scores],
  );
  const total = sorted.length;
  const dmin = total ? sorted[0] : 0;
  const dmax = total ? sorted[total - 1] : 1;
  const span = dmax - dmin || 1;

  const [inner, setInner] = useState<ScoreBandValue>({
    anchor: defaultValue?.anchor ?? r4(dmin + span / 2),
    pct: defaultValue?.pct ?? 7,
    mode: defaultValue?.mode ?? "near",
  });
  const v = value ?? inner;
  const { anchor, pct, mode } = v;

  /* scores 가 비동기로 늦게 도착한 경우, 최초 1회만 기준값을 가운데로 */
  const didCenter = useRef(total > 0 || defaultValue?.anchor !== undefined || value !== undefined);
  useEffect(() => {
    if (didCenter.current || total === 0) return;
    didCenter.current = true;
    setInner((s) => ({ ...s, anchor: r4(dmin + (dmax - dmin) / 2) }));
  }, [total, dmin, dmax]);

  const result = useMemo(() => computeBand(sorted, anchor, pct, mode), [sorted, anchor, pct, mode]);
  const emit = useCallback(
    (next: ScoreBandValue) => {
      if (value === undefined) setInner(next);
      onChange?.(next, computeBand(sorted, next.anchor, next.pct, next.mode));
    },
    [value, onChange, sorted],
  );

  const vRef = useRef(v);
  vRef.current = v;

  /* 히스토그램 */
  const hist = useMemo(() => {
    const b = new Array(BINS).fill(0) as number[];
    sorted.forEach((s) => {
      let i = Math.floor(((s - dmin) / span) * BINS);
      if (i >= BINS) i = BINS - 1;
      if (i < 0) i = 0;
      b[i]++;
    });
    return b;
  }, [sorted, dmin, span]);
  const hmax = useMemo(() => Math.max(1, ...hist), [hist]);

  /* 확대 창 (드래그 중에는 고정) */
  const [frozen, setFrozen] = useState<{ lo: number; hi: number } | null>(null);
  const pad = Math.max(result.eps * 2.4, span * 0.003);
  const zw = frozen ?? { lo: anchor - pad, hi: anchor + pad };
  const zSpan = zw.hi - zw.lo || 1;

  const posF = (s: number) => ((s - dmin) / span) * 100;
  const posZ = (s: number) => ((s - zw.lo) / zSpan) * 100;

  const zPoints = useMemo(() => {
    const a = lowerBound(sorted, zw.lo),
      b = upperBound(sorted, zw.hi);
    const sl = sorted.slice(a, b);
    const every = Math.ceil(sl.length / 400);
    return every > 1 ? sl.filter((_, i) => i % every === 0) : sl;
  }, [sorted, zw.lo, zw.hi]);

  /* 드래그 */
  const fullRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ kind: DragKind; lane: "full" | "zoom" } | null>(null);

  const toScore = (el: HTMLDivElement, clientX: number, dLo: number, dHi: number) => {
    const box = el.getBoundingClientRect();
    return r4(dLo + clamp((clientX - box.left) / box.width, 0, 1) * (dHi - dLo));
  };

  /** 손잡이 위치(score) → 그 폭을 담는 비율(%) */
  const pctForEps = (eps: number) => {
    const a = vRef.current.anchor;
    const c = upperBound(sorted, a + eps) - lowerBound(sorted, a - eps);
    return clamp((c / Math.max(1, total)) * 100, 0.01, 100);
  };

  const onFullDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || !fullRef.current) return;
    fullRef.current.setPointerCapture(e.pointerId);
    dragRef.current = { kind: "anchor", lane: "full" };
    emit({ ...vRef.current, anchor: toScore(fullRef.current, e.clientX, dmin, dmax) });
  };
  const onFullMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.lane !== "full" || !fullRef.current) return;
    emit({ ...vRef.current, anchor: toScore(fullRef.current, e.clientX, dmin, dmax) });
  };

  const onZoomDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || !zoomResizable || !zoomRef.current) return;
    const s = toScore(zoomRef.current, e.clientX, zw.lo, zw.hi);
    zoomRef.current.setPointerCapture(e.pointerId);
    setFrozen({ lo: zw.lo, hi: zw.hi });
    dragRef.current = { kind: s < vRef.current.anchor ? "lo" : "hi", lane: "zoom" };
    emit({ ...vRef.current, pct: pctForEps(Math.abs(s - vRef.current.anchor)) });
  };
  const onZoomMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.lane !== "zoom" || !zoomRef.current) return;
    const s = toScore(zoomRef.current, e.clientX, zw.lo, zw.hi);
    emit({ ...vRef.current, pct: pctForEps(Math.abs(s - vRef.current.anchor)) });
  };
  const endDrag = () => {
    dragRef.current = null;
    setFrozen(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || (e.key !== "ArrowLeft" && e.key !== "ArrowRight")) return;
    e.preventDefault();
    const step = (e.shiftKey ? 0.01 : 0.001) * (e.key === "ArrowRight" ? 1 : -1);
    if (e.altKey) emit({ ...v, pct: pctForEps(Math.max(0, result.eps + step)) });
    else emit({ ...v, anchor: r4(clamp(anchor + step, dmin, dmax)) });
  };

  const bandStyle = (l: number, r: number): React.CSSProperties => ({
    left: `${l}%`,
    width: `${Math.max(0.25, r - l)}%`,
  });

  const activePreset = presets.find((p) => Math.abs(pct - p) < 0.05) ?? null;

  return (
    // <Shell elevation={0} isDisabled={disabled} className={className} style={style} sx={sx}>
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: "16px 16px 12px" }}>
        <Typography className={className} sx={{ fontSize: 15, fontWeight: 500, lineHeight: 1.4 }}>
          경계값 샘플링
        </Typography>
        <Typography>
          기준값을 중심으로 선택된 비율만큼의 데이터를 샘플링합니다. 기준값과 비율을 조정하여 원하는 범위의 데이터를
          선택할 수 있습니다.
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* 컨트롤 */}
        <Box
          sx={{
            //   gridTemplateColumns: { xs: "1fr", md: "150px 1fr" },
            display: "flex",
            gap: { xs: 1.75, md: "22px" },
            alignItems: "end",
            p: "14px 16px 12px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Label sx={{ mb: "5px" }}>기준 score</Label>
            <NumField
              as={AnchorField}
              ariaLabel="기준 score"
              display={f4(anchor)}
              min={dmin}
              max={dmax}
              step={0.0001}
              disabled={disabled}
              onCommit={(n) => emit({ ...v, anchor: r4(n) })}
            />
          </Box>

          <Box sx={{ width: "30%" }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap", mb: "3px" }}>
              <Label>표시 비율</Label>
              <NumField
                as={PctField}
                ariaLabel="표시 비율 (%)"
                display={pct.toFixed(1)}
                min={0.01}
                max={100}
                step={0.1}
                disabled={disabled}
                onCommit={(n) => emit({ ...v, pct: n })}
              />
              <Caption>
                % · 목표 {result.k.toLocaleString()}개 / 선택 {result.count.toLocaleString()}개
              </Caption>
            </Box>

            <MacSlider
              size="small"
              min={0.1}
              max={100}
              step={0.1}
              value={clamp(pct, 0.1, 100)}
              disabled={disabled}
              valueLabelDisplay="auto"
              valueLabelFormat={(n) => `${Number(n).toFixed(1)}%`}
              aria-label="표시 비율"
              onChange={(_, n) => emit({ ...v, pct: Array.isArray(n) ? n[0] : n })}
              sx={{ mb: "7px" }}
            />

            <ToggleButtonGroup
              exclusive
              size="small"
              value={activePreset}
              disabled={disabled}
              onChange={(_, p) => p !== null && emit({ ...v, pct: p as number })}
              sx={{ display: "flex", flexWrap: "wrap", "& .MuiToggleButtonGroup-grouped": { m: 0 } }}
            >
              {presets.map((p) => (
                <PresetButton key={p} value={p} aria-label={p === 100 ? "전체" : `${p}%`}>
                  {p === 100 ? "전체" : `${p}%`}
                </PresetButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* 자 */}
        <Box
          sx={(theme) => ({
            display: "grid",
            gap: "5px",
            p: "12px 16px 14px",
            backgroundColor: mac(theme).ruler,
            borderTop: `.5px solid ${mac(theme).hair}`,
          })}
        >
          <Lane
            ref={fullRef}
            role="slider"
            tabIndex={0}
            aria-label="전체 분포에서 기준 score 위치"
            aria-valuemin={dmin}
            aria-valuemax={dmax}
            aria-valuenow={anchor}
            onPointerDown={onFullDown}
            onPointerMove={onFullMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onKeyDown={onKeyDown}
          >
            <LaneTag>전체 분포</LaneTag>
            <Bars>
              {hist.map((c, i) => {
                const center = dmin + ((i + 0.5) / BINS) * span;
                const inBand = center >= result.lo && center <= result.hi;
                return (
                  <HBar
                    key={i}
                    data-in={inBand ? "true" : "false"}
                    style={{ height: `${Math.max(3, Math.sqrt(c / hmax) * 94)}%` }}
                  />
                );
              })}
            </Bars>
            <Band style={bandStyle(posF(result.lo), posF(result.hi))} />
            <Grip style={{ left: `${posF(result.lo)}%`, height: 30 }}>
              <i style={{ height: 14, marginTop: 8 }} />
            </Grip>
            <Grip style={{ left: `${posF(result.hi)}%`, height: 30 }}>
              <i style={{ height: 14, marginTop: 8 }} />
            </Grip>
            <Anchor style={{ left: `${clamp(posF(anchor), 0, 100)}%` }} />
          </Lane>

          <AxisRow>
            <Caption>{f4(dmin)}</Caption>
            <Caption sx={{ fontFamily: SANS, whiteSpace: "normal", textAlign: "center" }}>
              전체 {total.toLocaleString()}건 · 기준선을 끌어 이동
            </Caption>
            <Caption>{f4(dmax)}</Caption>
          </AxisRow>

          <Lane
            ref={zoomRef}
            data-zoom="true"
            data-resizable={zoomResizable ? "true" : "false"}
            role={zoomResizable ? "slider" : undefined}
            aria-label={zoomResizable ? "밴드 폭 조절" : undefined}
            aria-valuenow={zoomResizable ? pct : undefined}
            onPointerDown={onZoomDown}
            onPointerMove={onZoomMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            <LaneTag>밴드 주변 확대</LaneTag>
            {zPoints.map((s, i) => {
              const inBand = s >= result.lo && s <= result.hi;
              return (
                <Tick
                  key={i}
                  data-in={inBand ? "true" : "false"}
                  style={{ left: `${posZ(s)}%`, top: inBand ? 7 : 11, bottom: inBand ? 7 : 11 }}
                />
              );
            })}
            <Band style={bandStyle(posZ(result.lo), posZ(result.hi))} />
            {zoomResizable && (
              <>
                <Grip style={{ left: `${posZ(result.lo)}%`, height: 24 }}>
                  <i style={{ height: 11, marginTop: 6.5 }} />
                </Grip>
                <Grip style={{ left: `${posZ(result.hi)}%`, height: 24 }}>
                  <i style={{ height: 11, marginTop: 6.5 }} />
                </Grip>
              </>
            )}
            <Anchor style={{ left: `${posZ(anchor)}%` }} />
          </Lane>

          <AxisRow>
            <Caption>{f4(zw.lo)}</Caption>
            <Caption sx={{ fontFamily: SANS, whiteSpace: "normal", textAlign: "center" }}>
              범위 {f4(result.lo)} ~ {f4(result.hi)} · 오차 ±{result.eps.toFixed(4)} · 이하{" "}
              {result.nBelow.toLocaleString()} / 초과 {result.nAbove.toLocaleString()}
            </Caption>
            <Caption>{f4(zw.hi)}</Caption>
          </AxisRow>
        </Box>
        {/* </Shell> */}
      </Box>
    </>
  );
}
